"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Music, Volume2, VolumeX, X } from "lucide-react";
import { fetchSoundtrack, type MediaType, type Soundtrack } from "@/lib/api";
import { useMessages } from "@/lib/i18n/client";
import { track } from "@/lib/tracking/track";
import messages from "./movie-soundtrack.i18n";

// Banda sonora en la ficha de Viva Cine: fragmento oficial de 30 s (Deezer)
// en bucle. Los navegadores no dejan que el sonido parta solo, así que:
// - la 1ª vez se pregunta con un aviso chico (no bloquea la página) — el "Sí"
//   cuenta como el toque que el navegador exige, y además es el
//   consentimiento: el audio se pide a Deezer (ve la IP) solo desde ahí;
// - la respuesta se recuerda en este navegador; con "sí", al abrir otra
//   ficha intenta sonar sola y, si el navegador lo bloquea (recarga sin
//   haber tocado nada), queda el botón "Toca para escuchar";
// - el botón 🔊/🔇 siempre queda a mano para silenciar o activar.

const PREF_KEY = "cv-viva-cine-music";
// Avisa a la banda sonora que empezó el trailer (ver movie-trailer).
export const TRAILER_PLAY_EVENT = "cv:trailer-play";

type Pref = "on" | "off" | null;

function readPref(): Pref {
  try {
    const value = localStorage.getItem(PREF_KEY);
    return value === "on" || value === "off" ? value : null;
  } catch {
    return null;
  }
}

function savePref(value: "on" | "off"): void {
  try {
    localStorage.setItem(PREF_KEY, value);
  } catch {
    // Modo privado / almacenamiento bloqueado: solo se vuelve a preguntar.
  }
}

export function MovieSoundtrack({ movieId, mediaType }: { movieId: number; mediaType: MediaType }) {
  const t = useMessages(messages);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retriedRef = useRef(false);
  const [soundtrack, setSoundtrack] = useState<Soundtrack | null>(null);
  const [pref, setPref] = useState<Pref>(null);
  // Si la página abrió con "sí" guardado, el <audio> intenta sonar solo
  // (autoPlay). Si el navegador lo bloquea, nunca llega onPlay y queda
  // "Toca para escuchar".
  const [autoPlay, setAutoPlay] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSoundtrack(movieId, mediaType).then((s) => {
      if (cancelled) return;
      const saved = readPref();
      setPref(saved);
      setAutoPlay(saved === "on");
      setSoundtrack(s);
    });
    return () => {
      cancelled = true;
    };
  }, [movieId, mediaType]);

  // play() dentro del toque de la persona: el navegador lo permite.
  const play = useCallback(() => {
    audioRef.current?.play().catch(() => setPlaying(false));
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  // El trailer y la banda sonora no suenan a la vez.
  useEffect(() => {
    window.addEventListener(TRAILER_PLAY_EVENT, pause);
    return () => window.removeEventListener(TRAILER_PLAY_EVENT, pause);
  }, [pause]);

  // Al salir de la ficha (navegación interna) la música se corta.
  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, [soundtrack]);

  // El link del fragmento vence a los ~15 min: si falla, se pide uno nuevo
  // una sola vez (y suena si estaba activada).
  const onAudioError = async () => {
    if (retriedRef.current) {
      setSoundtrack(null);
      return;
    }
    retriedRef.current = true;
    const fresh = await fetchSoundtrack(movieId, mediaType);
    setAutoPlay(pref === "on");
    setSoundtrack(fresh);
  };

  if (!soundtrack) return null;

  // "Sí" guardado pero no está sonando: el navegador bloqueó el autoplay.
  const blocked = pref === "on" && !playing;

  const answer = (accepted: boolean) => {
    const value = accepted ? "on" : "off";
    savePref(value);
    setPref(value);
    track("soundtrack_prompt_answer", { tmdb_id: movieId, accepted });
    if (accepted) play();
  };

  const toggle = () => {
    const on = !playing;
    savePref(on ? "on" : "off");
    setPref(on ? "on" : "off");
    track("soundtrack_toggle", { tmdb_id: movieId, on });
    if (on) play();
    else pause();
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={soundtrack.previewUrl}
        loop
        autoPlay={autoPlay}
        // Nada se descarga de Deezer hasta que suena (autoplay con "sí"
        // guardado, o play() dentro del toque).
        preload="none"
        onError={onAudioError}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />

      {pref === null ? (
        <div
          role="dialog"
          aria-labelledby="soundtrack-prompt-title"
          className="fixed bottom-4 right-4 left-4 z-40 mx-auto max-w-sm rounded-2xl border border-border bg-surface p-4 shadow-xl sm:left-auto"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Music className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p id="soundtrack-prompt-title" className="text-sm font-semibold">
                {t.promptTitle}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t.promptBody}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                ♪ {soundtrack.title} — {soundtrack.artist}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => answer(true)}
                  className="inline-flex h-8 cursor-pointer items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90"
                >
                  {t.accept}
                </button>
                <button
                  type="button"
                  onClick={() => answer(false)}
                  className="inline-flex h-8 cursor-pointer items-center rounded-full border border-border px-3 text-xs font-medium hover:bg-muted"
                >
                  {t.decline}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => answer(false)}
              aria-label={t.decline}
              className="cursor-pointer rounded-full p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-border bg-surface/95 py-1.5 pl-1.5 pr-3 shadow-lg backdrop-blur">
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? t.mute : t.play}
            aria-pressed={playing}
            className={`flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition ${
              playing ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
            } ${blocked ? "animate-pulse" : ""}`}
          >
            {playing ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <div className="min-w-0 text-xs leading-tight">
            <p className="max-w-[12rem] truncate font-medium">
              {blocked ? t.tapToListen : soundtrack.title}
            </p>
            <a
              href={soundtrack.deezerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground hover:underline"
            >
              {soundtrack.artist ? `${soundtrack.artist} · ` : ""}
              {t.via}
            </a>
          </div>
        </div>
      )}
    </>
  );
}
